import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  AlertTriangle,
  CheckCircle,
  Clock,
  Briefcase,
  FileText,
  ExternalLink,
  X,
} from "lucide-react";
import { Tag, Image, Divider, message, Spin, Modal, Button } from "antd";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import {
  useRoundIssuesStore,
  IssueStatus,
} from "../../../store/RoundIssuesStore";
import { useOrgUsersStore } from "../../../store/store";

const IssueDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentIssue,
    loading: issueLoading,
    error,
    fetchIssueById,
    updateIssue,
  } = useRoundIssuesStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  const [statusLoading, setStatusLoading] = useState(false);

  // Document Viewer State
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<{
    url: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (id) {
      fetchIssueById(id);
      if (!orgUsersData.length) fetchOrgUsers();
    }
  }, [id, fetchIssueById, fetchOrgUsers, orgUsersData.length]);

  const handleStatusChange = async (newStatus: IssueStatus) => {
    if (!id) return;
    setStatusLoading(true);
    try {
      const success = await updateIssue(id, { status: newStatus });
      if (success) {
        message.success(`Status updated to ${newStatus}`);
      } else {
        message.error("Failed to update status");
      }
    } finally {
      setStatusLoading(false);
    }
  };

  if (issueLoading || !currentIssue) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{ zIndex: 9999 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Critical":
        return "red";
      case "High":
        return "orange";
      case "Medium":
        return "gold";
      case "Low":
        return "green";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Open":
        return "blue";
      case "In Progress":
        return "purple";
      case "Resolved":
        return "success";
      case "Closed":
        return "default";
      default:
        return "default";
    }
  };

  const assignedPersonName = currentIssue.assignedToUserId
    ? orgUsersData.find(
        (u: any) =>
          u._id === currentIssue.assignedToUserId ||
          u.id === currentIssue.assignedToUserId,
      )?.firstName
    : "Unassigned";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {currentIssue.issueNumber}
            </h2>
            <p className="text-gray-500 text-sm mt-1">{currentIssue.title}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title & Status */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {currentIssue.title}
                    </h3>
                    <p className="text-gray-600 mt-2">
                      {currentIssue.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Tag color={getSeverityColor(currentIssue.severity)}>
                      {currentIssue.severity}
                    </Tag>
                    <Tag color={getStatusColor(currentIssue.status)}>
                      {currentIssue.status}
                    </Tag>
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-blue-200">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm font-medium text-gray-900">
                        {currentIssue.createdAt
                          ? new Date(
                              currentIssue.createdAt,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Time</p>
                      <p className="text-sm font-medium text-gray-900">
                        {currentIssue.createdAt
                          ? new Date(currentIssue.createdAt).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" },
                            )
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-blue-500" />
                  Location Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                      Premises
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {currentIssue.location?.premises || "N/A"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                      Region
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {currentIssue.location?.region || "N/A"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                      Area / Floor
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {currentIssue.location?.area || "N/A"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                      Department
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {currentIssue.location?.department || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Photo Evidence & Attachments */}
              {currentIssue.photoEvidence &&
                currentIssue.photoEvidence.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText size={20} className="text-green-500" />
                      Evidence & Attachments (
                      {currentIssue.photoEvidence.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {currentIssue.photoEvidence.map((url, index) => {
                        const filename = url.split("/").pop() || "";
                        const decodedFilename = decodeURIComponent(filename);
                        const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(
                          decodedFilename,
                        );

                        return (
                          <div key={index} className="relative group">
                            {isImage ? (
                              <Image
                                src={url}
                                alt={`Evidence ${index + 1}`}
                                className="rounded-lg object-cover w-full h-32"
                                preview={{
                                  mask: "View",
                                }}
                              />
                            ) : (
                              <div
                                className="border-2 border-gray-100 rounded-lg h-32 flex flex-col items-center justify-center bg-gray-50 hover:border-blue-400 cursor-pointer transition-all"
                                onClick={() => {
                                  setViewingDoc({
                                    url,
                                    name: decodedFilename,
                                  });
                                  setViewerOpen(true);
                                }}
                              >
                                <FileText size={40} className="text-blue-500" />
                                <p className="text-[10px] text-gray-500 mt-2 px-2 text-center truncate w-full">
                                  {decodedFilename}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Assignment Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <User size={16} />
                  Assignment
                </h3>

                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <p className="text-xs text-gray-500 font-semibold">
                      Assigned Team
                    </p>
                    <p className="font-medium text-gray-900 mt-1">
                      {currentIssue.assignTo}
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <p className="text-xs text-gray-500 font-semibold">
                      Assigned Person
                    </p>
                    <p className="font-medium text-gray-900 mt-1">
                      {assignedPersonName}
                    </p>
                  </div>
                </div>
              </div>

              {/* Linked Items Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <FileText size={16} />
                  Linked Items
                </h3>

                <div className="space-y-3">
                  {currentIssue.workOrderId ? (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2 text-green-800 mb-1">
                        <CheckCircle size={16} />
                        <span className="font-medium text-sm">Work Order</span>
                      </div>
                      <p className="text-xs text-green-600 truncate">
                        {currentIssue.workOrderId}
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
                      <p className="text-xs text-gray-500">
                        No Work Order linked
                      </p>
                    </div>
                  )}

                  {currentIssue.linkedChecklistItemId && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                      <p className="text-xs text-gray-500 font-semibold">
                        Checklist Item
                      </p>
                      <p className="text-xs text-indigo-700 mt-1 font-mono break-all">
                        {currentIssue.linkedChecklistItemId}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#505d6f] border-t border-gray-200 flex justify-end gap-3">
          <button onClick={() => navigate(-1)}>Close</button>
          <button
            onClick={() =>
              navigate(`/safety-facility-rounds/issues/edit/${id}`)
            }
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          >
            Edit Issue
          </button>
          {currentIssue.status !== "Closed" && (
            <button
              className="flex items-center justify-center font-semibold gap-3 bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700 border-0"
              onClick={() =>
                handleStatusChange(
                  currentIssue.status === "Resolved" ? "Closed" : "Resolved",
                )
              }
            >
              {currentIssue.status === "Resolved"
                ? "Close Issue"
                : "Mark as Resolved"}
            </button>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      <Modal
        open={viewerOpen}
        onCancel={() => setViewerOpen(false)}
        footer={null}
        width={1000}
        centered
        className="document-viewer-modal"
        styles={{ body: { padding: 0, height: "80vh", overflow: "hidden" } }}
      >
        <div className="h-full flex flex-col bg-gray-50">
          <div className="p-4 border-b flex justify-between items-center bg-white">
            <h3 className="font-semibold text-lg">{viewingDoc?.name}</h3>
          </div>
          <div className="flex-1 overflow-auto p-4 flex justify-center">
            {viewingDoc && (
              <DocumentViewer file={viewingDoc.url} name={viewingDoc.name} />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IssueDetails;
